import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { FxRateControl } from "@/components/FxRateControl";
import { PaystackAccountCheck } from "@/components/PaystackAccountCheck";
import { GatewayOptions } from "@/components/GatewayOptions";

const GW = [["stripe", "Stripe"], ["paystack", "Paystack"], ["flutterwave", "Flutterwave"]];
const PAID = ["success", "completed"];

export function PaymentsHealthCard() {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/payments/health").then(({ data }) => setH(data)).catch(() => setH({}));
  useEffect(() => { load(); }, []);

  const reconcile = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/reconcile");
      toast.success(`Checked ${data.checked} pending · fulfilled ${data.fulfilled} · closed ${data.closed}`);
      load();
    } catch { toast.error("Reconciliation failed"); }
    finally { setBusy(false); }
  };

  if (!h) return null;
  const stats = h.last_30_days || {};
  const sum = (gw) => Object.entries(stats[gw] || {}).reduce((acc, [s, v]) => acc + (PAID.includes(s) ? v.usd : 0), 0);
  const count = (gw) => Object.entries(stats[gw] || {}).reduce((acc, [s, v]) => acc + (PAID.includes(s) ? v.count : 0), 0);
  const pending = (gw) => (stats[gw]?.pending?.count || 0) + (stats[gw]?.initiated?.count || 0);
  const lr = h.last_reconcile || {};

  return (
    <div className="rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="payments-health-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-[#34C759]" /><div className="text-xs uppercase tracking-widest text-neutral-500">Payments</div></div>
          <h3 className="font-black text-xl tracking-tight mt-1">Gateway health &amp; revenue (30d)</h3>
        </div>
        <Button onClick={reconcile} disabled={busy} size="sm" variant="outline" data-testid="payments-reconcile-btn" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5 hover:text-white">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Reconcile now
        </Button>
      </div>
      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        {GW.map(([k, label]) => {
          const g = h.gateways?.[k] || {};
          const ok = g.mode && g.mode !== "missing";
          return (
            <div key={k} className="rounded-lg border border-[#27272A] bg-[#0A0A0A] p-4" data-testid={`gateway-${k}`}>
              <div className="flex items-center justify-between">
                <div className="font-bold">{label}</div>
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${!ok ? "text-[#FF3B30] border-[#FF3B30]/40" : g.mode === "live" ? "text-[#34C759] border-[#34C759]/40" : "text-[#FF9F0A] border-[#FF9F0A]/40"}`} data-testid={`gateway-mode-${k}`}>
                  {ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />} {g.mode || "missing"}
                </span>
              </div>
              <div className="mt-3 font-mono font-bold text-2xl text-[#34C759] tabular-nums" data-testid={`gateway-revenue-${k}`}>${sum(k).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <div className="text-xs text-neutral-500">{count(k)} paid · {pending(k)} pending · {g.currency}</div>
              {k === "stripe" && g.webhook_secret === false && <div className="mt-2 text-[11px] text-[#FF9F0A]" data-testid="stripe-webhook-warning">Webhook secret not set — relying on success-page polling + hourly reconcile</div>}
              {k === "paystack" && <PaystackAccountCheck />}
            </div>
          );
        })}
      </div>
      <FxRateControl />
      <GatewayOptions />
      <div className="mt-4 text-xs text-neutral-500" data-testid="payments-last-reconcile">
        {lr.at ? <>Last reconciliation {new Date(lr.at).toLocaleString()} · checked {lr.checked} · fulfilled {lr.fulfilled} · closed {lr.closed} · still pending {lr.still_pending}{lr.errors ? ` · errors ${lr.errors}` : ""}</> : "Reconciliation hasn't run yet (hourly schedule)."}
      </div>
    </div>
  );
}
